import * as amplitude from '@amplitude/analytics-browser';
import whenDomReady from 'when-dom-ready';
const COOKIE_NAME = 'CookieControl';
const Events = {
    ELIGIBILITY_BTN: 'click sf.com check eligibility button',
    HOME_VISIT: 'visit sf.com home page',
    PRODUCT_BTN: 'click sf.com product button',
    SEARCH_FOCUS: 'click sf.com employer search bar',
    SEARCH_RESULT: 'match sf.com employer search',
    SEARCH_TRIGGER: 'trigger sf.com employer search',
    SIGN_IN_BTN: 'click sf.com sign in button',
};
const TriggerEvents = {
    autoSuggest: 'click employer name',
    button: "click let's go",
};
function initialise() {
    if (window.AmpConfig && !window.AmpConfig.cookiePolicyEnabled) {
        initTracking();
    }
    else {
        initCookieListener();
        initNoTrackingRedirects();
    }
}
/**
 * Check whether the necessary cookie has been accepted or not, initialise based on the result.
 * If permissions cookie doesn't exist, loop until it's been configured.
 */
function initCookieListener() {
    const cookieAccepted = getCookiePolicyValue();
    if (cookieAccepted === null) {
        return document.addEventListener('analytical-cookies-accepted', () => {
            initTracking();
        });
    }
    else if (cookieAccepted === true) {
        return initTracking();
    }
    else {
        return;
    }
}
function initTracking() {
    amplitude
        .init(window.AmpConfig.apiKey, undefined, {
        serverZone: amplitude.Types.ServerZone.EU,
    })
        .promise.then(() => {
        initHomepageVisit();
        initEmployeeSearchTracking();
        initialiseButtonClickEvents();
    });
}
/** Redirect based on eligibility result if tracking is not available */
function initNoTrackingRedirects() {
    const elgibilityResult = document.querySelector('.js-eligibility-result');
    if (elgibilityResult) {
        followRedirect(elgibilityResult.value);
    }
}
/** Identify the client location based on the homepage URL */
function initHomepageVisit() {
    const homepageCheck = document.querySelector('.js-home-identifier');
    if (homepageCheck) {
        const location = window.location.pathname.split('/')[1].toUpperCase();
        amplitude.track(Events.HOME_VISIT, {
            clientLocation: location,
        });
    }
}
function initEmployeeSearchTracking() {
    /** Identify when the client focuses on the Eligibility Search text input */
    document.addEventListener('eligibility-input-focus', () => {
        amplitude.track(Events.SEARCH_FOCUS, {
            'url of page': window.location.href,
        });
    });
    /**
     * Identify when the client uses the Eligibility Search, and specify
     * whether they submitted the form or clicked on an autoSuggest result
     */
    document.addEventListener('eligibility-search', (e) => {
        if (isCustomEvent(e)) {
            amplitude.track(Events.SEARCH_TRIGGER, {
                'trigger method': TriggerEvents[e.detail],
            });
        }
    });
    /**
     * Identify when the Eligibility Search has been completed and
     * pass on whether or not a result was returned or not
     *
     * For the legacy search, redirect the user to the resulting
     * employer subdomain page once the tracking has been triggered
     */
    document.addEventListener('eligibility-result', (e) => {
        if (isCustomEvent(e)) {
            const { type, redirect } = e.detail;
            amplitude
                .track(Events.SEARCH_RESULT, {
                'result of employer match': type,
            })
                .promise.then(() => {
                followRedirect(redirect);
            });
        }
    });
    /**
     * LEGACY EMPLOYEE SEARCH ONLY
     *
     * When a legacy search is completed, rather than the redirect being the result the user
     * will now be shown a message that they are being redirected in place of the form.
     * From the resulting detail, trigger the `eligibility-result` with the necessary detail
     * to complete the user journey.
     */
    const elgibilityResult = document.querySelector('.js-eligibility-result');
    if (elgibilityResult) {
        const event = new CustomEvent('eligibility-result', {
            detail: { type: 'fwh', redirect: elgibilityResult.value },
        });
        document.dispatchEvent(event);
    }
}
function initialiseButtonClickEvents() {
    /**
     * Identify when the client interacts with a Check Eligibility button, and pass
     * through the area of the site the clicked button exists in
     */
    const eligibilityCheck = document.querySelectorAll('.js-eligibility-check-btn');
    eligibilityCheck.forEach(item => {
        item.addEventListener('click', () => {
            /** Ensure link is configured to point at Eligibility Checker before tracking location of button */
            if (item.href.includes('/#eligibilityChecker')) {
                const where = item.getAttribute('data-where');
                amplitude.track(Events.ELIGIBILITY_BTN, {
                    'button type': where,
                });
            }
        });
    });
    /**
     * Identify when the client interacts with the Sign In button, pass through the page
     * URL the user is on when the button is clicked, disable the button and redirect the
     * user to the Sign In page once the tracking event has been sent
     */
    const signInButton = document.querySelectorAll('.js-sign-in-btn');
    signInButton.forEach(item => {
        item.addEventListener('click', e => {
            e.preventDefault();
            if (item.getAttribute('disabled') === null) {
                item.setAttribute('disabled', 'disabled');
                amplitude
                    .track(Events.SIGN_IN_BTN, {
                    'url of button click': window.location.href,
                })
                    .promise.then(() => {
                    followRedirect(item.href);
                });
            }
        });
    });
    /**
     * Identify when the client interacts with a Product link, pass through the
     * area of the site the clicked button exists in as well as the Product type,
     * disable the button and redirect the user to the Product page once the tracking
     * event has been sent.
     */
    const productButton = document.querySelectorAll('.js-product-btn');
    productButton.forEach(item => {
        item.addEventListener('click', e => {
            e.preventDefault();
            if (item.getAttribute('disabled') === null) {
                const where = item.getAttribute('data-where');
                const type = item.getAttribute('data-product-type');
                item.setAttribute('disabled', 'disabled');
                if (type === 'overview') {
                    followRedirect(item.href);
                }
                else {
                    amplitude
                        .track(Events.PRODUCT_BTN, {
                        'button position': where,
                        product: type,
                    })
                        .promise.then(() => {
                        followRedirect(item.href);
                    });
                }
            }
        });
    });
}
/** Check the event triggered is a Custom Event with the necessary detail value is defined */
function isCustomEvent(event) {
    return 'detail' in event;
}
/** Only follow redirect if the url includes the domain name */
function followRedirect(redirect) {
    if (redirect && /(salaryfinance.com|salaryfinance.dev|localhost)/.test(redirect)) {
        window.location.href = redirect;
    }
}
/** Get cookie policy cookie values, return the result of optionalAnalytics policy  */
function getCookiePolicyValue() {
    const policy = getCookie(COOKIE_NAME);
    if (policy === '') {
        return null;
    }
    if (JSON.parse(policy)?.optionalCookies?.optionalAnalytics === 'accepted') {
        return true;
    }
    else {
        return false;
    }
}
function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts !== undefined && parts.length === 2) {
        return parts
            ?.pop()
            ?.split(';')
            .shift();
    }
    else {
        return '';
    }
}
whenDomReady().then(initialise);
